import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  LayoutDashboard, Package, FileText, Settings, LogOut, 
  Menu, X, Plus, Pencil, Trash2, Search
} from 'lucide-react';
import peptLogo from '../../assets/pept-logo-cropped.png';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminPeptides() {
  const { token, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [peptides, setPeptides] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPeptides();
  }, [token]);

  const fetchPeptides = async () => {
    try {
      const res = await fetch(`${API}/api/admin/peptides`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPeptides(data);
      }
    } catch (err) {
      console.error('Error fetching peptides:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (catNo) => {
    if (!window.confirm(`Are you sure you want to delete ${catNo}?`)) return;
    
    try {
      const res = await fetch(`${API}/api/admin/peptides/${catNo}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setPeptides(peptides.filter(p => p.cat_no !== catNo));
      }
    } catch (err) {
      console.error('Error deleting peptide:', err);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  const filteredPeptides = peptides.filter(p => 
    p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.cat_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const menuItems = [
    { label: 'Dashboard', icon: LayoutDashboard, path: '/admin/dashboard' },
    { label: 'Peptides', icon: Package, path: '/admin/peptides', active: true },
    { label: 'COA Library', icon: FileText, path: '/admin/coas' },
    { label: 'Site Settings', icon: Settings, path: '/admin/settings' },
  ];

  return (
    <div className="min-h-screen bg-neutral-900 flex">
      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-neutral-800 border-r border-neutral-700 transform transition-transform lg:translate-x-0 lg:static
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="p-4 border-b border-neutral-700">
          <img src={peptLogo} alt="PEP-T.COM" className="h-10" />
          <p className="text-neutral-400 text-xs mt-1">Admin Panel</p>
        </div>

        <nav className="p-4 space-y-1">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                item.active 
                  ? 'bg-red-600 text-white' 
                  : 'text-neutral-300 hover:bg-neutral-700 hover:text-white'
              }`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-neutral-700">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 text-neutral-400 hover:text-red-400 w-full transition-colors"
          >
            <LogOut className="w-5 h-5" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 min-h-screen">
        {/* Top bar */}
        <header className="bg-neutral-800 border-b border-neutral-700 px-4 py-4 flex items-center justify-between lg:px-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 text-neutral-400 hover:text-white lg:hidden"
            >
              {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Peptide Management
            </h1>
          </div>
          <span className="text-neutral-400 text-sm">
            {filteredPeptides.length} peptides
          </span>
        </header>

        {/* Content */}
        <div className="p-4 lg:p-6 space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
            <input
              type="text"
              placeholder="Search by name, catalog number, or category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-neutral-800 border border-neutral-700 rounded-sm text-white placeholder-neutral-500"
            />
          </div>

          {/* Table */}
          <div className="bg-neutral-800 border border-neutral-700 rounded-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-neutral-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-neutral-400">Cat No</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-neutral-400">Name</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-neutral-400 hidden md:table-cell">Category</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-neutral-400 hidden lg:table-cell">Concentration</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-neutral-400">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-700">
                  {loading ? (
                    <tr>
                      <td colSpan="5" className="px-4 py-8 text-center text-neutral-500">
                        Loading peptides...
                      </td>
                    </tr>
                  ) : filteredPeptides.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-4 py-8 text-center text-neutral-500">
                        No peptides found
                      </td>
                    </tr>
                  ) : (
                    filteredPeptides.map((peptide) => (
                      <tr key={peptide.cat_no} className="hover:bg-neutral-700/50">
                        <td className="px-4 py-3 text-sm font-mono text-red-400">
                          {peptide.cat_no}
                        </td>
                        <td className="px-4 py-3 text-sm text-white">
                          {peptide.name}
                        </td>
                        <td className="px-4 py-3 text-sm text-neutral-400 hidden md:table-cell">
                          {peptide.category}
                        </td>
                        <td className="px-4 py-3 text-sm text-neutral-400 hidden lg:table-cell">
                          {peptide.concentration}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Link
                              to={`/peptide/${peptide.cat_no}`}
                              target="_blank"
                              className="p-2 text-neutral-400 hover:text-blue-400"
                              title="View on site"
                            >
                              <Pencil className="w-4 h-4" />
                            </Link>
                            <button
                              onClick={() => handleDelete(peptide.cat_no)}
                              className="p-2 text-neutral-400 hover:text-red-400"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <p className="text-neutral-500 text-sm">
            Note: To add new peptides or make major edits, please contact support. 
            This interface currently supports viewing and basic management.
          </p>
        </div>
      </main>
    </div>
  );
}
